import { camelCase } from './helpers';

export type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

export type KeysToSnakeCase<T> =
  T extends Array<infer U>
    ? Array<{
        [K in keyof U as CamelToSnakeCase<string & K>]: U[K] extends object
          ? KeysToSnakeCase<U[K]>
          : U[K];
      }>
    : {
        [K in keyof T as CamelToSnakeCase<string & K>]: T[K] extends object
          ? KeysToSnakeCase<T[K]>
          : T[K];
      };

export type CamelCase<S extends string> = S extends `${infer P1}_${infer P2}${infer P3}`
  ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
  : Lowercase<S>;

export type KeysToCamelCase<T> =
  T extends Array<infer U>
    ? Array<{
        [K in keyof U as CamelCase<string & K>]: U[K] extends object ? KeysToCamelCase<U[K]> : U[K];
      }>
    : {
        [K in keyof T as CamelCase<string & K>]: T[K] extends object ? KeysToCamelCase<T[K]> : T[K];
      };

export const objectCamelize = <T extends object>(record: T): KeysToCamelCase<T> => {
  if (Array.isArray(record)) {
    return <KeysToCamelCase<T>>record.map(v => objectCamelize(v));
  } else if (typeof record === 'object' && record !== null) {
    return <KeysToCamelCase<T>>Object.entries(record).reduce(
      (acc, [key, value]) => {
        acc[camelCase(key)] = objectCamelize(value);
        return acc;
      },
      <Record<string, unknown>>{},
    );
  } else {
    return record;
  }
};
